import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { useUserRoles, useInfluencerPackagePermissions } from '@anytag/auth';
import { ReadPackageAccount } from '@anytag/graphql';
import { TableCell } from '@anytag/shared-atoms';
import { useTranslateCountry } from '@anytag/shared-hooks';
import { SpreadButton } from '@anytag/shared-molecules';
import { formatIntNumber } from '@anytag/shared-utils';

interface Props {
  canDeletePackage?: boolean;
  onDeletePackage: (data: { id: number; name: string }) => void;
}

export const usePackageListTable = ({ canDeletePackage, onDeletePackage }: Props) => {
  const { t } = useTranslation();
  const { tc } = useTranslateCountry();
  const { isAdminStaff } = useUserRoles();
  const { hideEditPackageBtn, hideDeletePackageBtn } = useInfluencerPackagePermissions();

  const columns: ColumnDef<ReadPackageAccount>[] = [
    {
      accessorKey: 'name',
      header: t<string>('HeaderColumn.Package'),
      cell: ({ row }) => (
        <TableCell.Link
          label={row.original.name}
          to="/package/$id/content"
          params={{ id: row.original.id.toString() }}
        />
      )
    },
    {
      accessorKey: 'company',
      meta: { hidden: !isAdminStaff },
      header: t<string>('HeaderColumn.Company'),
      cell: ({ row }) => {
        const { companies } = row.original;

        return (
          <TableCell.Text
            lines={2}
            value={companies?.map((el) => el?.name).join(', ') || '-'}
            tooltip={{
              help: companies?.length ? (
                <ul>{companies?.map((company) => <li key={company?.id}>{company?.name}</li>)}</ul>
              ) : null
            }}
          />
        );
      }
    },
    {
      accessorKey: 'country',
      header: t<string>('HeaderColumn.Country'),
      cell: ({ row }) => <TableCell.Text value={tc(row.original.country.name)} />
    },
    {
      accessorKey: 'accounts',
      header: t<string>('HeaderColumn.Account'),
      meta: { align: 'right' },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.accounts || 0)} />
    },
    {
      size: 32,
      accessorKey: 'delete',
      meta: { align: 'center', hidden: !canDeletePackage },
      cell: ({ row }) => (
        <SpreadButton
          collapseFrom={0}
          css={{ width: 'max-content', margin: '0 auto', justifyContent: 'center' }}
          moreButtonProps={{ disabled: hideEditPackageBtn && hideDeletePackageBtn }}
          options={[
            {
              icon: 'edit',
              label: t('Edit'),
              to: '/package/$id',
              hidden: hideEditPackageBtn,
              tooltip: { help: t('Button.Edit') },
              params: { id: row.original.id.toString() }
            },
            {
              icon: 'delete',
              label: t('Delete'),
              disabled: !canDeletePackage,
              hidden: hideDeletePackageBtn,
              tooltip: { help: t('Button.Delete') },
              onClick: () => onDeletePackage({ id: row.original.id, name: row.original.name })
            }
          ]}
        />
      )
    }
  ];

  return { columns };
};
